import { useState, useEffect } from 'react';
import { TEXT_KEYS, getText, TextKey } from '../data/textKeys';
import { publicApiService } from '../services/publicApi';

interface UseTextsReturn {
  getText: (key: string) => string;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Hook personnalisé pour gérer les textes dynamiques
 * Récupère les textes depuis l'API et fournit une fonction getText
 */
export const useTexts = (): UseTextsReturn => {
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTexts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer les textes depuis l'API
      const response = await publicApiService.getTexts();

      // Convertir la réponse en objet clé-valeur
      const textsMap: Record<string, string> = {};
      response.forEach((text: any) => {
        textsMap[text.cle_info] = text.contenu || text.valeur ||  '';
      });

      setTexts(textsMap);
    } catch (err) {
      console.error('Erreur lors du chargement des textes:', err);
      setError('Erreur lors du chargement des textes');
      // En cas d'erreur, on utilise les valeurs par défaut
      setTexts({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTexts();
  }, []);

  const getTextValue = (key: string): string => {
    return getText(key, texts);
  };

  const refresh = () => {
    loadTexts();
  };

  return {
    getText: getTextValue,
    loading,
    error,
    refresh
  };
};

/**
 * Hook simplifié qui ne charge pas depuis l'API mais utilise seulement les valeurs par défaut
 * Utile pour les composants qui n'ont pas besoin de textes dynamiques
 */
export const useStaticTexts = () => {
  return {
    getText: (key: string) => getText(key),
    loading: false,
    error: null,
    refresh: () => {}
  };
};

/**
 * Fonction utilitaire pour obtenir un texte avec fallback
 * @param key - Clé du texte
 * @param fallback - Texte de fallback si la clé n'existe pas
 * @param texts - Objet contenant les textes (optionnel)
 * @returns Le texte correspondant ou le fallback
 */
export const getTextWithFallback = (
  key: string, 
  fallback: string, 
  texts?: Record<string, string>
): string => {
  const text = getText(key, texts);
  return text === key ? fallback : text;
};

/**
 * Fonction utilitaire pour obtenir tous les textes d'une catégorie
 * @param category - Catégorie des textes
 * @param texts - Objet contenant les textes (optionnel)
 * @returns Objet contenant tous les textes de la catégorie
 */
export const getCategoryTexts = (
  category: string, 
  texts?: Record<string, string>
): Record<string, string> => {
  const categoryKeys = Object.entries(TEXT_KEYS)
    .filter(([_, textKey]) => textKey.category === category)
    .map(([key, _]) => key);
  
  const result: Record<string, string> = {};
  categoryKeys.forEach(key => {
    result[key] = getText(key, texts);
  });
  
  return result;
};

/**
 * Fonction utilitaire pour formater un texte avec des paramètres
 * @param key - Clé du texte
 * @param params - Paramètres à remplacer dans le texte
 * @param texts - Objet contenant les textes (optionnel)
 * @returns Le texte formaté
 */
export const getFormattedText = (
  key: string, 
  params: Record<string, string | number>, 
  texts?: Record<string, string>
): string => {
  let text = getText(key, texts);
  
  Object.entries(params).forEach(([param, value]) => {
    text = text.replace(new RegExp(`{${param}}`, 'g'), String(value));
  });
  
  return text;
};

/**
 * Hook pour récupérer les informations du site (logo, etc.)
 */
export const useSiteInfo = () => {
  const [siteInfos, setSiteInfos] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSiteInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer les informations du site depuis l'API
      const response = await publicApiService.getTexts();

      // Convertir la réponse en objet clé-objet
      const infoMap: Record<string, any> = {};
      response.forEach((info: any) => {
        infoMap[info.cle_info] = info;
      });

      setSiteInfos(infoMap);
    } catch (err) {
      console.error('Erreur lors du chargement des informations du site:', err);
      setError('Erreur lors du chargement des informations du site');
      setSiteInfos({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSiteInfo();
  }, []);

  const getSiteInfo = (key: string): any => {
    return siteInfos[key] || null;
  };

  const refresh = () => {
    loadSiteInfo();
  };

  return {
    getSiteInfo,
    siteInfos,
    loading,
    error,
    refresh
  };
};

export default useTexts;
